if (!customElements.get("product-quick-view-drawer")) {
  /**
   * Quick view drawer for product previews.
   * Uses pointerdown preload to start fetching ~100ms before click.
   */
  customElements.define(
    "product-quick-view-drawer",
    class ProductQuickViewDrawer extends Drawer {
      /** Max number of cached product pages */
      static MAX_CACHE = 10;

      /** Quick view alternate template suffix */
      static VIEW = "quick-view";

      constructor() {
        super();

        this.quickViewContent = this.querySelector(".drawer-content");
        this.htmlCache = new Map();

        // Preload on pointerdown (fires ~100ms before click)
        this.onPointerdown = this.onPointerdown.bind(this);
        document.addEventListener("pointerdown", this.onPointerdown);

        // Invalidate cache when cart changes (availability/quantity may differ)
        webvista.subscribe(PUB_SUB_EVENTS.cartUpdate, () =>
          this.htmlCache.clear(),
        );
      }

      disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener("pointerdown", this.onPointerdown);
      }

      /**
       * Start fetching product HTML on pointerdown for faster open.
       * @param {PointerEvent} event
       */
      onPointerdown(event) {
        const opener = event.target.closest(
          `modal-opener[aria-controls="${this.id}"]`,
        );
        if (!opener) return;

        const productUrl = opener.getAttribute("data-product-url");
        if (!productUrl) return;

        // Skip if already cached or pending
        if (this.htmlCache.has(productUrl)) return;
        if (this.preloadUrl === productUrl && this.preloadPromise) return;

        this.preloadUrl = productUrl;
        if (this.fetchController) this.fetchController.abort();
        this.fetchController = new AbortController();
        this.preloadPromise = webvista.fetchHtml(
          this.getQuickViewUrl(productUrl),
          this.fetchController.signal,
        );
      }

      /**
       * Open quick view drawer and fetch product content.
       * @param {HTMLElement} opener - The element that triggered the open
       */
      show(opener) {
        super.show(opener);

        let productUrl;
        if (opener) {
          productUrl = opener.getAttribute("data-product-url");
        } else if (window.Shopify.designMode) {
          // Design mode preview product
          productUrl = this.getAttribute("data-product-url");
        }

        if (!productUrl) return;

        // Update the "View Details" link
        const productDetailsLink = document.getElementById(
          "Product-Details-Link",
        );
        if (productDetailsLink) productDetailsLink.href = productUrl;

        this.renderContent(productUrl);
      }

      hide() {
        this.endLoading();

        // Abort pending fetch
        if (this.fetchController) this.fetchController.abort();
        this.preloadPromise = null;
        this.preloadUrl = null;
        this.quickViewContent.innerHTML = "";

        super.hide();
      }

      /**
       * Render product content from cache or fetch.
       * @param {string} url - Product page URL
       */
      renderContent(url) {
        // Instant render from cache (no loading state needed)
        if (this.htmlCache.has(url)) {
          this.renderFromCache(url);
          return;
        }

        this.startLoading();

        // Reuse preloaded fetch or start a new one
        let fetchPromise;
        if (this.preloadUrl === url && this.preloadPromise) {
          fetchPromise = this.preloadPromise;
        } else {
          if (this.fetchController) this.fetchController.abort();
          this.fetchController = new AbortController();
          fetchPromise = webvista.fetchHtml(
            this.getQuickViewUrl(url),
            this.fetchController.signal,
          );
        }

        // Clear preload state
        this.preloadPromise = null;
        this.preloadUrl = null;

        fetchPromise
          .then((html) => {
            this.handleHTML(html);

            // Cache processed HTML for instant re-open
            this.cacheHTML(url, this.quickViewContent.innerHTML);

            // Trap focus within drawer
            webvista.trapFocus(this);
          })
          .catch((error) => {
            if (error.name === "AbortError") return;

            this.quickViewContent.innerHTML = "";
          })
          .finally(() => {
            this.endLoading();
          });
      }

      /**
       * Render product from cached processed HTML (skips fetch + DOM processing).
       * @param {string} url - Cached product URL
       */
      renderFromCache(url) {
        this.setInnerHTML(this.quickViewContent, this.htmlCache.get(url));

        this.eagerLoadImages();
        webvista.initToolTips();

        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }

        this.preventVariantURLSwitching();
        webvista.trapFocus(this);
      }

      /**
       * Store processed HTML with LRU eviction.
       * @param {string} url - Product URL key
       * @param {string} html - Processed HTML string
       */
      cacheHTML(url, html) {
        // LRU: delete existing to refresh insertion order
        if (this.htmlCache.has(url)) this.htmlCache.delete(url);

        // Evict oldest entry if at capacity
        if (this.htmlCache.size >= ProductQuickViewDrawer.MAX_CACHE) {
          const oldest = this.htmlCache.keys().next().value;
          this.htmlCache.delete(oldest);
        }

        this.htmlCache.set(url, html);
      }

      /**
       * Append ?view=quick-view to use the lightweight template.
       * @param {string} url - Product page URL
       * @returns {string} URL with view parameter
       */
      getQuickViewUrl(url) {
        const separator = url.includes("?") ? "&" : "?";
        return `${url}${separator}view=${ProductQuickViewDrawer.VIEW}`;
      }

      startLoading() {
        if (this.openedBy) {
          this.openedBy.setAttribute("aria-disabled", true);
          this.openedBy.classList.add("loading");
        }

        // Show loading placeholder
        const template = this.querySelector("template");
        if (template) {
          this.quickViewContent.innerHTML = template.innerHTML;
        }
      }

      endLoading() {
        if (this.openedBy) {
          this.openedBy.removeAttribute("aria-disabled");
          this.openedBy.classList.remove("loading");
        }
      }

      handleHTML(html) {
        const productHtml = html.querySelector('[id^="Main-Product-"]');
        if (!productHtml) return;

        // Replace HTML and execute scripts
        this.setInnerHTML(this.quickViewContent, productHtml.innerHTML);

        // Eagerly load all images ��?no benefit from lazy loading in quick view,
        // and hidden variant images (display:none) won't trigger IntersectionObserver
        this.eagerLoadImages();
        webvista.initToolTips();

        // Initialize Shopify dynamic checkout buttons
        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }

        this.preventVariantURLSwitching();
      }

      /**
       * Set innerHTML and re-inject script tags to allow execution.
       * @param {HTMLElement} element - Target container
       * @param {string} html - HTML string to inject
       */
      setInnerHTML(element, html) {
        element.innerHTML = html;

        // Re-inject script tags to allow execution
        // Scripts inserted via innerHTML are disabled by default
        element.querySelectorAll("script").forEach((oldScriptTag) => {
          const newScriptTag = document.createElement("script");
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(
            document.createTextNode(oldScriptTag.innerHTML),
          );
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
      }

      /**
       * Prevent variant picker from updating browser URL.
       */
      preventVariantURLSwitching() {
        const variantPicker = this.quickViewContent.querySelector(
          "high-variant-selects",
        );
        if (!variantPicker) return;

        variantPicker.setAttribute("data-update-url", "false");
      }

      /**
       * Eagerly load all lazy images in the drawer.
       * Quick view images are small (15rem), so lazy loading has no benefit.
       * This also fixes hidden variant images (display:none) that would never
       * trigger IntersectionObserver.
       */
      eagerLoadImages() {
        this.querySelectorAll(
          ".image-lazy-loading:not(.image-lazy-loaded)",
        ).forEach((img) => {
          if (img.hasAttribute("data-srcset")) {
            img.setAttribute("srcset", img.dataset.srcset);
            img.removeAttribute("data-srcset");
            if (img.complete) {
              img.classList.add("image-lazy-loaded");
            } else {
              img.addEventListener(
                "load",
                () => img.classList.add("image-lazy-loaded"),
                { once: true },
              );
            }
          } else {
            img.classList.add("image-lazy-loaded");
          }
        });
      }

    },
  );
}
